// libs
import React, { useEffect, useState } from 'react';
// components
import FileUploadLayout from '../layout/preview/FileUploadLayout';
import FileUploader from '../ArtifactUploaderInput/FileUploader';
import FilePreviewListLayout from '../layout/list/FilePreviewListLayout';
// services
import useArtifactInputHelper from './artifactsInputHelper';
import useArtifactValidationService from './artifactValidationService';
import { ErrorMessage } from 'framework/ui/layout';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';

const concatErrors = (errors1: null[], errors2: Nullable<string[]>) => {
  const toArr = (errors: null[] | Nullable<string[]>) => errors || [];
  return [...toArr(errors1), ...toArr(errors2)].filter(v => v);
};

export type ArtifactUploaderInputProps = {
  canAddFromClipboard?: boolean;
  showBtnImage?: boolean;
  uploadBtnText?: string;
  disabled?: boolean;
  uploadFile?: (params: {
    url: string;
    data: FormData;
    eventTarget?: AbortSignal;
    onProgress?: (progress: { loaded: number; total: number }) => void;
  }) => Promise<{ response: string }>;
  label?: string;
  source?: string;
  artifactTypeId?: number;
  isVisible?: boolean | ((data: FormFieldsData | any) => boolean);
  maxElements?: number;
  value?: any;
  tooltip?: string;
  isRequired?: boolean;
  isNewDocumentStorage?: boolean;
  addArtifactToContext?: (id: string, file: File) => void;
  removeArtifactFromContext?: (id: string) => void;
  onChangeCallback?: (values: FormFieldsData) => void;
  fileExtensions?: string;
  maximumSize?: number;
  isCandidateService?: boolean;
  isProjectService?: boolean;
  isRiskService?: boolean;
  isStaffingService?: boolean;
  budgetId?: StringOrNumber;
};

const ArtifactUploaderInput: React.FC<ArtifactUploaderInputProps> = ({
  canAddFromClipboard = false,
  showBtnImage = true,
  uploadBtnText = 'Upload or drag files',
  disabled = false,
  uploadFile,
  isNewDocumentStorage,
  isCandidateService = false,
  isProjectService = false,
  isRiskService = false,
  isStaffingService = false,
  budgetId,
  ...props
}) => {
  const [errorMsg, setErrorMsg] = useState(null);

  const {
    getLabel,
    getValue,
    getAcceptExtensions,
    addArtifactToContext,
    initializeValue,
    removeArtifactFromContext,
    getTooltip,
    getHelpText,
    getSource,
    getMaxElements,
    getArtifactType,
    getMaxMb,
    getIsRequired,
    getErrorMessages,
    getClearValidationCallback,
    isVisible,
  } = useArtifactInputHelper(props);

  const clearValidation = getClearValidationCallback();

  const artifactsIdsList = (getValue() || []) as any[];

  /*
   * On create page input should have [] value by default.
   */
  useEffect(() => {
    if (!getValue()) {
      initializeValue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { validate } = useArtifactValidationService({
    previewsCount: artifactsIdsList?.length,
    source: getSource(),
    maxMb: getMaxMb(),
    fileTypes: getAcceptExtensions(),
  });
  const isAllowedToAddMoreFiles =
    (!getMaxElements() && getMaxElements() !== 0) || getMaxElements() - artifactsIdsList.length > 0;

  return (
    <FileUploadLayout
      label={getLabel() as string}
      error={concatErrors([errorMsg], getErrorMessages()) as Nullable<ErrorMessage[]>}
      isRequired={getIsRequired()}
      tooltip={getTooltip() as string}
      source={getSource()}
      helpText={getHelpText()}
      disabled={disabled}
      isVisible={isVisible()}
      previews={
        <FilePreviewListLayout
          isNewDocumentStorage={isNewDocumentStorage}
          artifactsIdsList={artifactsIdsList}
          onRemove={id => removeArtifactFromContext(id)}
          disabled={disabled}
          isCandidateService={isCandidateService}
          isProjectService={isProjectService}
          isRiskService={isRiskService}
          isStaffingService={isStaffingService}
          budgetId={budgetId}
        />
      }
      fileUploder={
        isAllowedToAddMoreFiles ? (
          <FileUploader
            isNewDocumentStorage={isNewDocumentStorage}
            disabled={disabled}
            canAddFromClipboard={canAddFromClipboard}
            artifactTypeId={getArtifactType()}
            uploadCallback={(id, file) => {
              addArtifactToContext(id, file);
              clearValidation();
            }}
            acceptExtensions={getAcceptExtensions()}
            validateFunc={validate}
            validationCallback={error => {
              setErrorMsg(error);
            }}
            source={getSource()}
            uploadFile={uploadFile}
            isRiskService={isRiskService}
            uploadBtnText={uploadBtnText}
            showBtnImage={showBtnImage}
            maxElements={getMaxElements()}
          />
        ) : null
      }
    />
  );
};

export default ArtifactUploaderInput;
